import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import AccountLink from '~/components/app/AccountLink';
import GeneralAdminLink from '~/components/app/GeneralAdminLink';
import PlaceholderLink from '~/components/app/PlaceholderLink';
import WebsiteSettingsLink from '~/components/app/WebsiteSettingsLink';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import ExpandableNavSection from '~/components/patterns/navigations/ExpandableNavSection';
import NavigationIcon, {
	NavigationIconType,
} from '~/components/patterns/icons/NavigationIcon';

import {
	type messages
} from '../MainNavigation';



type Props = {
	accountId: CK.AccountId | null,
	collapseAllExpandables?: () => void,
	effectiveHomeAccountId: CK.AccountId | null,
	isAnyExpandableExpanded?: boolean,
	isInternalExpanded?: boolean,
	kingdomAdminFeatures?: {
		areVisible: boolean,
	},
	messages: typeof messages,
	onInternalToggle?: () => void,
	openSwitchAccountModal?: () => void,
	websiteId: CK.WebsiteId | null,
};

/**
 * Build secondary navigation items (Integrations, Settings, Account)
 *
 * Returns an array of JSX elements for the secondary navigation section
 */
export function buildNewSecondaryItems(props: Props): Array<React.ReactElement> {
	const {
		accountId,
		collapseAllExpandables,
		effectiveHomeAccountId,
		isAnyExpandableExpanded = false,
		isInternalExpanded = false,
		kingdomAdminFeatures,
		messages,
		onInternalToggle,
		openSwitchAccountModal,
		websiteId,
	} = props;

	const showInternal = kingdomAdminFeatures?.areVisible === true;

	const items: Array<React.ReactElement> = [
		// Integrations
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="integrations-item"
		>
			<PlaceholderLink
				onClick={collapseAllExpandables}
				sectionName="integrations"
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={(
								<NavigationIcon
									size={20}
									type={NavigationIconType.Integrations}
								/>
							)}
							isActive={isActive && !isAnyExpandableExpanded}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.integrations} />
							)}
						/>
					);
				}}
			</PlaceholderLink>
		</li>,
		// Settings
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="settings-item"
		>
			<WebsiteSettingsLink
				onClick={collapseAllExpandables}
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Settings}
							/>
						)}
						isActive={isActive && !isAnyExpandableExpanded}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.settings} />
						)}
					/>
				)}
			</WebsiteSettingsLink>
		</li>,
		// Account (per clickdummy structure)
		<li
			className="main-nav__item"
			data-secondary-nav-item="true"
			key="account-item"
		>
			<AccountLink
				accountId={accountId ?? effectiveHomeAccountId ?? undefined}
				onClick={collapseAllExpandables}
			>
				{({ isActive, isDimmed }) => (
					<MenuItemLayout
						icon={(
							<NavigationIcon
								size={20}
								type={NavigationIconType.Account}
							/>
						)}
						isActive={isActive && !isAnyExpandableExpanded}
						isDimmed={isDimmed}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.account} />
						)}
					/>
				)}
			</AccountLink>
		</li>,
	];

	// Internal (collapsible) - only shown for kingdom admins when admin features are visible
	if (showInternal && onInternalToggle && openSwitchAccountModal) {
		items.push(
			<ExpandableNavSection
				dataAttributes={{ 'data-secondary-nav-item': 'true', 'data-internal-nav': 'true' }}
				icon={(
					<NavigationIcon
						size={20}
						type={NavigationIconType.LockInSquare}
					/>
				)}
				isExpanded={isInternalExpanded}
				isSubItemsHidden={false}
				key="internal-section"
				label={(
					<FormattedMessage {...messages.internal} />
				)}
				onToggle={onInternalToggle}
			>
				{/* Switch Account */}
				<li
					className="main-nav__item"
					key="internal-switch-account-item"
				>
					<MenuItemLayout
						icon={<></>}
						isActive={false}
						isDimmed={false}
						isLabelHidden={false}
						label={(
							<FormattedMessage {...messages.switchAccount} />
						)}
						onClickCallback={() => openSwitchAccountModal()}
					/>
				</li>
				{/* General Admin */}
				<li
					className="main-nav__item"
					key="internal-general-admin-item"
				>
					<GeneralAdminLink>
						{({ isActive, isDimmed }) => (
							<MenuItemLayout
								icon={<></>}
								isActive={isActive}
								isDimmed={isDimmed}
								isLabelHidden={false}
								label={(
									<FormattedMessage {...messages.generalAdmin} />
								)}
							/>
						)}
					</GeneralAdminLink>
				</li>
			</ExpandableNavSection>,
		);
	}

	return items;
}
